import type { ApiResponse } from '@shared/types'
import type { AxiosError } from 'axios'
import { humanize } from 'inflection'
import { uniqBy } from 'lodash'
import { logger } from 'v2/utils/logger'

interface ApiError {
  message: string
  path: string
}

export function extractErrorMessages(errors: unknown): ApiError[] {
  const messages: ApiError[] = []

  function traverse(obj: unknown, path = '') {
    try {
      if (Array.isArray(obj)) {
        obj.forEach((item, index) => traverse(item, `${path}[${index}]`))
      } else if (typeof obj === 'object' && obj !== null) {
        Object.entries(obj).forEach(([key, value]) => {
          traverse(value, path ? `${path}.${key}` : key)
        })
      } else if (typeof obj === 'string') {
        messages.push({ message: obj, path })
      }
    } catch (e) {
      logger.error(e as string)
    }
  }

  traverse(errors)
  return Object.assign([...messages], {
    toString: () =>
      uniqBy(messages, 'message')
        .map((e) => e.message)
        .join(', '),
  })
}

export const capitalizeFirstLetter = (text: string) => {
  if (!text) return text
  return text
    .split('. ')
    .map((sentence) => sentence.charAt(0).toUpperCase() + sentence.slice(1))
    .join('. ')
}

// Error strings from the backend sometimes wrap an upstream JSON payload
// (e.g. Meta Cloud API) inside a prefix like
// `Client.do(): error = ..., response: {"error":{"message":"..."}}`.
// Pull out the user-facing message so the toast stays readable.
const extractEmbeddedErrorMessage = (text: string): string | null => {
  const match = text.match(/\{.*\}/)
  if (!match) return null
  try {
    const parsed = JSON.parse(match[0])
    const metaError = parsed?.error ?? parsed
    if (typeof metaError === 'string') return metaError
    if (metaError && typeof metaError === 'object') {
      const candidate =
        (metaError as Record<string, unknown>).error_user_msg ||
        (metaError as Record<string, unknown>).message
      return typeof candidate === 'string' ? candidate : null
    }
    return null
  } catch {
    return null
  }
}

const cleanErrorText = (text: string): string => extractEmbeddedErrorMessage(text) || text

/**
 * Coerce an unknown error value to displayable text, or null when it carries no
 * message.
 *
 * Interpolating an object straight into a template literal yields
 * "[object Object]", which humanize() then lowercases into the literal
 * "[object object]" — reported by customers on the toll-free verification
 * screen when the backend answered with a nested object instead of a string.
 * Objects are only usable if a real message can be pulled out of them; anything
 * else returns null so callers fall through to a generic message.
 */
const printableErrorText = (value: unknown): string | null => {
  if (typeof value === 'string') return value.trim() || null
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  if (value === null || value === undefined) return null
  if (typeof value === 'object') {
    const extracted = extractErrorMessages(value).toString()
    return extracted || null
  }
  return null
}

export const TF_NOT_UPDATABLE_MESSAGE =
  'This request can no longer be updated. Please create a new request.'

const TF_NOT_UPDATABLE_TOKEN = 'TF_NOT_UPDATABLE'

/**
 * Detects (and resolves the display message for) a terminally-rejected
 * toll-free verification that can no longer be updated. Reconciled backend
 * contract (SER-xxxx): a resubmit on a terminal TF request returns HTTP 400
 * with `errors.global_error` prefixed by the literal `TF_NOT_UPDATABLE:`
 * token followed by a human message, e.g.
 *   "TF_NOT_UPDATABLE: This toll-free verification can no longer be updated
 *    because it was Rejected by the carrier. Please create a new request."
 * There is no separate error_code/action_required field — the token IS the
 * message prefix.
 *  1. PRIMARY: candidate string (checked in order: errors.global_error,
 *     error, message) starts with the token — strip the prefix and show the
 *     carrier-supplied reason verbatim.
 *  2. FALLBACK: the standalone toll-free PATCH bypass path still forwards
 *     the raw upstream error verbatim, with no token — matched by its known
 *     wording and shown as the generic TF_NOT_UPDATABLE_MESSAGE.
 */
const resolveTfNotUpdatable = (
  err: AxiosError<ApiResponse<unknown>> | null | undefined,
): { message: string } | null => {
  const data = err?.response?.data as
    | (Record<string, unknown> & { errors?: Record<string, unknown> })
    | undefined
  if (!data) return null

  const candidate = [data.errors?.global_error, data.error, data.message].find(
    (v): v is string => typeof v === 'string',
  )
  if (!candidate) return null

  if (candidate.startsWith(TF_NOT_UPDATABLE_TOKEN)) {
    const separatorIndex = candidate.indexOf(': ')
    const strippedMessage = separatorIndex !== -1 ? candidate.slice(separatorIndex + 2).trim() : ''
    return { message: strippedMessage || TF_NOT_UPDATABLE_MESSAGE }
  }

  // Matches both upstream spellings: Porter's raw camelCase
  // "tollFreeVerification" and the hyphenated "Toll-free verification" that
  // contacto-core rewrites it to before returning. The original single-word
  // pattern missed the rewritten form, so the friendly message was silently
  // skipped for the wording production actually sends.
  if (
    /toll[\s-]?free\s?verification/i.test(candidate) &&
    /not allowed to be updated/i.test(candidate)
  ) {
    return { message: TF_NOT_UPDATABLE_MESSAGE }
  }

  return null
}

export const isTfNotUpdatableError = (
  err: AxiosError<ApiResponse<unknown>> | null | undefined,
): boolean => resolveTfNotUpdatable(err) !== null

export const getErrorText = (err: AxiosError<ApiResponse<unknown>>) => {
  const tfNotUpdatable = resolveTfNotUpdatable(err)
  if (tfNotUpdatable) return tfNotUpdatable.message

  // Check if error is a direct string
  if (typeof err === 'string') {
    return capitalizeFirstLetter(humanize(`${err}` as string)) || 'Something went wrong'
  }

  const responseData = err.response?.data
  const errors = responseData?.errors

  // Check for error_data.description field
  if (errors?.error_data?.description) {
    return capitalizeFirstLetter(humanize(`${errors.error_data.description}`))
  }

  // Check for global_error.error when global_error is an object
  if (
    typeof errors?.global_error === 'object' &&
    (errors.global_error?.error || (errors.global_error as Record<string, unknown>)?.Error)
  ) {
    const globalError =
      errors.global_error.error || (errors.global_error as Record<string, unknown>).Error
    // If error is an object, extract nested error messages
    if (typeof globalError === 'object' && globalError !== null) {
      const extracted = extractErrorMessages(globalError)
      if (extracted.length > 0) {
        // Format errors: add field name only if the original value was an array
        const formattedErrors = extracted.map((e) => {
          // Check if path contains array index (e.g., "hangup_url[0]") to determine if value was array
          const hasArrayIndex = e.path.includes('[')
          if (hasArrayIndex) {
            const fieldName = e.path.split('[')[0] // Get field name before array index
            const humanizedField = humanize(fieldName)
            return `${humanizedField} ${e.message.toLowerCase()}`
          }
          // If value was string, don't include field name
          return e.message
        })
        return capitalizeFirstLetter(formattedErrors.join(', '))
      }
    }
    // If error is a string or primitive, use it directly
    const globalErrorText = printableErrorText(globalError)
    if (globalErrorText) {
      return capitalizeFirstLetter(humanize(cleanErrorText(globalErrorText)))
    }
  }

  // Check for various error fields in response data. Pick the first PRINTABLE
  // candidate, not the first truthy one: a `||` chain would select an
  // unprintable object (e.g. global_error: { code: 400 }) and discard a
  // perfectly good responseData.message sitting behind it.
  const candidateText = [errors?.global_error, responseData?.message, responseData?.error]
    .map(printableErrorText)
    .find((text): text is string => Boolean(text))
  if (candidateText) {
    return capitalizeFirstLetter(humanize(cleanErrorText(candidateText)))
  }

  // Extract error messages from responseData.data if it exists
  if (responseData?.data) {
    const extracted = extractErrorMessages(responseData.data)
    if (extracted.toString()) {
      return capitalizeFirstLetter(humanize(extracted.toString()))
    }
  }

  // Extract error messages from errors array
  if (Array.isArray(errors)) {
    const extracted = extractErrorMessages(errors)
    if (extracted.toString()) {
      return capitalizeFirstLetter(humanize(cleanErrorText(extracted.toString())))
    }
  }

  // Extract error messages from errors object
  if (typeof errors === 'object' && errors !== null) {
    const extracted = extractErrorMessages(errors)
    if (extracted.toString()) {
      return capitalizeFirstLetter(humanize(cleanErrorText(extracted.toString())))
    }
  }

  // Use errors directly if it's a truthy value
  const errorsText = printableErrorText(errors)
  if (errorsText) {
    return capitalizeFirstLetter(humanize(cleanErrorText(errorsText)))
  }

  // Fall back to axios error message
  if (err.message) {
    return capitalizeFirstLetter(humanize(err.message))
  }

  // Use responseData.data as last resort
  const dataText = printableErrorText(responseData?.data)
  if (dataText) {
    return capitalizeFirstLetter(humanize(dataText))
  }

  // Default fallback message
  return 'Something went wrong'
}
